import { lazy, Suspense } from 'react';
import { generatePath, Navigate, Route } from 'react-router-dom';
import { useDealController } from './useDealController.tsx';
import { CreateProgrammeDrawer } from '@dealstack/frontend/app/pages/Project/DealDashboard/CreateProgrammeDrawer/CreateProgrammeDrawer';

import { DealContext } from '@dealstack/frontend/app/pages/Project/DealDashboard/DealContext';
import { Flex } from '@mantine/core';
import { DEAL_ROUTES, ROUTES } from '@dealstack/frontend/app/pages/Routing/routes';
import { DealDetails } from '@dealstack/frontend/app/pages/Project/DealDetails/DealDetails';
import { DealEventLog } from '@dealstack/frontend/app/pages/Project/DealEventLog/DealEventLog.tsx';
import { DealEventLogCurrent } from '@dealstack/frontend/app/pages/Project/DealEventLog/DealEventLogCurrent.tsx';
import { DealEventMessage } from '@dealstack/frontend/app/pages/Project/DealEventLog/DealEventMessage.tsx';
import { DealSettings } from '@dealstack/frontend/app/pages/Project/DealSettings/DealSettings.tsx';
import { Dashboard as DocumentLibraryDashboard } from '@dealstack/frontend/app/pages/Project/DocumentLibrary/Dashboard';
import { MIP as Mip } from '@dealstack/frontend/app/pages/Project/MIP/MIP';
import { Valuations } from '@dealstack/frontend/app/pages/Valuations/Valuations';
import { SentryRoutes } from '@dealstack/frontend/shared/sentry.ts';
import { CapTablePage } from '@dealstack/frontend/app/pages/Project/Cap/CapTablePage/CapTablePage.tsx';
import { ReactFlowProvider } from '@xyflow/react';
import { CapChartPage } from '@dealstack/frontend/app/pages/Project/Cap/CapChartPage/CapChartPage.tsx';
import { CapManageEntityFlow as CapEntityManageFlow } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/CapManageEntityFlow.tsx';
import {
	CapHeaderContext,
	useCapPageHeaderController,
} from '@dealstack/frontend/app/pages/Project/Cap/common/CapPageHeader/useCapPageHeaderController.tsx';
import { FullscreenLoader } from '@dealstack/frontend/shared/components/loaders/FullLoaderScreenLoader/FullLoaderScreenLoader';
import { useDealstackFeatureFlag } from '@dealstack/frontend/shared/hooks/useDealstackFeatureFlag/useDealstackFeatureFlag.hook';
import { FEATURE_FLAG } from '@dealstack/shared/lib/common/featureFlag';

const BatchAnalysisPage = lazy(() =>
	import('@dealstack/frontend/app/pages/Project/Cap/BatchAnalysis/BatchAnalysisPage').then(
		(m) => ({ default: m.BatchAnalysisPage })
	)
);

const DealDashboard = () => {
	const dealController = useDealController();
	const capDateFilterController = useCapPageHeaderController();
	const batchAnalysisEnabled = useDealstackFeatureFlag(
		FEATURE_FLAG.CAP_TRANSACTION_FROM_DOCUMENT
	).value;
	const eventLogFlag = useDealstackFeatureFlag(FEATURE_FLAG.DEAL_EVENT_LOG);
	const behindEventLogFlag = (element: JSX.Element) => {
		if (eventLogFlag.loading) {
			return null;
		}

		return eventLogFlag.value ? element : <Navigate to={DEAL_ROUTES.DETAILS} />;
	};

	return (
		<Flex h="100%" direction="column">
			<Flex direction="column" sx={{ flexGrow: 1, height: '100%', overflow: 'hidden' }}>
				<DealContext.Provider value={dealController}>
					<SentryRoutes>
						<Route path={`${DEAL_ROUTES.DETAILS}`} element={<DealDetails />} />
						<Route
							path={`${DEAL_ROUTES.PEOPLE}?/*`}
							element={
								<CapHeaderContext.Provider value={capDateFilterController}>
									<CapEntityManageFlow />
								</CapHeaderContext.Provider>
							}
						/>
						<Route
							path={`${DEAL_ROUTES.DOCUMENT_LIBRARY}`}
							element={<DocumentLibraryDashboard />}
						/>
						<Route
							path={DEAL_ROUTES.EVENT_LOG}
							element={behindEventLogFlag(<DealEventLog />)}
						/>
						<Route
							path={DEAL_ROUTES.EVENT_LOG_MESSAGE}
							element={behindEventLogFlag(<DealEventMessage />)}
						/>
						<Route
							path={DEAL_ROUTES.EVENT_LOG_CURRENT}
							element={behindEventLogFlag(<DealEventLogCurrent />)}
						/>
						<Route path={`${DEAL_ROUTES.SETTINGS}/*`} element={<DealSettings />} />
						<Route path={`${DEAL_ROUTES.MIP}/*`} element={<Mip />} />
						<Route
							path={DEAL_ROUTES.CAP_TABLE}
							element={
								<CapHeaderContext.Provider value={capDateFilterController}>
									<CapTablePage />
								</CapHeaderContext.Provider>
							}
						/>
						<Route
							path={DEAL_ROUTES.CAP_CHART}
							element={
								<CapHeaderContext.Provider value={capDateFilterController}>
									<ReactFlowProvider>
										<CapChartPage />
									</ReactFlowProvider>
								</CapHeaderContext.Provider>
							}
						/>
						<Route path={`${DEAL_ROUTES.WATERFALL}/*`} element={<Valuations />} />
						<Route
							path={`${DEAL_ROUTES.BATCH_ANALYSIS}`}
							element={
								batchAnalysisEnabled ? (
									<Suspense fallback={<FullscreenLoader name="suspense" />}>
										<BatchAnalysisPage />
									</Suspense>
								) : (
									<Navigate to={DEAL_ROUTES.DETAILS} />
								)
							}
						/>
						<Route
							path="*"
							element={
								<Navigate
									to={generatePath(ROUTES.DEAL, {
										dealId: dealController.dealId,
									})}
								/>
							}
						/>
					</SentryRoutes>

					<CreateProgrammeDrawer
						dealId={dealController.dealId}
						opened={dealController.quickCreateOpen}
						isCreating={dealController.isQuickCreating}
						onClose={dealController.closeQuickCreate}
						onCreate={dealController.onQuickCreate}
					/>
				</DealContext.Provider>
			</Flex>
		</Flex>
	);
};

export { DealDashboard };
